import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Search, AlertTriangle, Code2, BarChart3, ArrowRight,
  CheckCircle, ChevronRight, Zap, Globe, Lock, Users, Building2,
  TrendingUp, Eye, Activity, Star, Play,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

/* ─────────────────────────────────────────────────────────────────────────
   Scroll-reveal hook — triggers .in class when element enters viewport
───────────────────────────────────────────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in'); }),
      { threshold: 0.12 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ─────────────────────────────────────────────────────────────────────────
   Dashboard Mockup — hero right panel
───────────────────────────────────────────────────────────────────────── */
const MOCK_SCANS = [
  { addr: '0x742d…F8a3', risk: 78, level: 'High',   color: 'bg-red-500' },
  { addr: '0x1A9c…2D71', risk: 45, level: 'Medium', color: 'bg-yellow-400' },
  { addr: '0x9f3B…C412', risk: 18, level: 'Low',    color: 'bg-green-500' },
  { addr: '0x33Ae…8B90', risk: 62, level: 'Medium', color: 'bg-yellow-400' },
];

function DashboardMockup() {
  const [scanning, setScanning] = useState(false);
  const [done,     setDone]     = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setScanning(true),  1200);
    const t2 = setTimeout(() => { setScanning(false); setDone(true); }, 2800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div className="relative w-full rounded-2xl overflow-hidden border border-white/[0.08] bg-[#080d18] shadow-2xl shadow-black/60">
      {/* Titlebar */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#0a1020] border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Shield size={13} className="text-[#00E5FF]" />
          <span className="text-[11px] font-semibold text-slate-300">ZenithCipher Dashboard</span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-medium text-green-400">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-blink" />
          LIVE
        </div>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-3 gap-2 p-3">
        {[
          { label: 'Total Scans', value: '1,247', sub: '+12 today',   color: 'text-[#00E5FF]' },
          { label: 'High Risk',   value: '89',    sub: '7.1% rate',   color: 'text-red-400'   },
          { label: 'API Calls',   value: '8.4K',  sub: 'this month',  color: 'text-[#7C3AED]' },
        ].map(({ label, value, sub, color }) => (
          <div key={label} className="bg-white/[0.03] rounded-lg p-2.5 border border-white/[0.05]">
            <div className={`text-base font-bold tabular-nums ${color}`}>{value}</div>
            <div className="text-[9px] text-slate-500 mt-0.5">{label}</div>
            <div className="text-[9px] text-slate-600">{sub}</div>
          </div>
        ))}
      </div>

      {/* Scanner input */}
      <div className="px-3 pb-2">
        <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.07] rounded-lg px-3 py-2">
          <Search size={11} className="text-slate-600 flex-shrink-0" />
          <span className="text-[10px] text-slate-600 font-mono flex-1 truncate">
            {done ? '0x742d35Cc6634C0532925…' : scanning ? '0x742d35Cc…' : 'Enter wallet address…'}
          </span>
          <div className={`text-[9px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 transition-all ${
            done    ? 'bg-red-500/20 text-red-400' :
            scanning? 'bg-[#00E5FF]/15 text-[#00E5FF] animate-pulse' :
                      'bg-white/[0.06] text-slate-500'
          }`}>
            {done ? 'HIGH RISK' : scanning ? 'SCANNING…' : 'SCAN'}
          </div>
        </div>
      </div>

      {/* Recent scans table */}
      <div className="px-3 pb-3">
        <div className="text-[9px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5 px-0.5">
          Recent Scans
        </div>
        <div className="space-y-1">
          {MOCK_SCANS.map((s, i) => (
            <div key={i} className="flex items-center gap-2 bg-white/[0.025] rounded-lg px-2.5 py-2 group hover:bg-white/[0.05] transition-colors">
              <span className="font-mono text-[10px] text-slate-400 w-20 flex-shrink-0">{s.addr}</span>
              <div className="flex-1 h-1 bg-white/[0.07] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${s.color} transition-all duration-1000`}
                  style={{ width: `${s.risk}%` }}
                />
              </div>
              <span className="text-[10px] tabular-nums text-slate-400 w-5 text-right flex-shrink-0">{s.risk}</span>
              <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 ${
                s.level === 'High'   ? 'bg-red-500/15 text-red-400' :
                s.level === 'Medium' ? 'bg-yellow-500/15 text-yellow-400' :
                                       'bg-green-500/15 text-green-400'
              }`}>
                {s.level}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Section components
───────────────────────────────────────────────────────────────────────── */

/* 1 ── HERO */
function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">

      {/* Animated grid background */}
      <div className="absolute inset-0 hero-grid opacity-60 pointer-events-none" />

      {/* Gradient blobs */}
      <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-[#00E5FF]/6 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-[#7C3AED]/6 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00E5FF]/3 rounded-full blur-[160px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-14 items-center">

          {/* Left — copy */}
          <div>
            {/* Eyebrow badge */}
            <div className="animate-fade-in inline-flex items-center gap-2 border border-[#00E5FF]/25 bg-[#00E5FF]/8 text-[#00E5FF] text-xs font-semibold px-3.5 py-1.5 rounded-full mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-blink" />
              Web3 Security Intelligence Platform
            </div>

            <h1 className="animate-fade-in delay-100 text-5xl sm:text-6xl font-bold text-white leading-[1.08] tracking-tight mb-6">
              Blockchain{' '}
              <span className="text-shimmer">Intelligence</span>
              {' '}&amp;{' '}
              <br />
              Wallet Risk
              <span className="block text-[#00E5FF]"> Analysis</span>
            </h1>

            <p className="animate-fade-in delay-200 text-slate-400 text-lg leading-relaxed max-w-lg mb-10">
              Analyze crypto wallets, detect suspicious activity, and gain deep blockchain
              insights in seconds. Trusted by Web3 teams and security professionals worldwide.
            </p>

            {/* CTAs */}
            <div className="animate-fade-in delay-300 flex flex-col sm:flex-row gap-3 mb-12">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 bg-[#00E5FF] hover:bg-[#00cce5] text-black font-bold px-7 py-3.5 rounded-xl transition-all shadow-lg shadow-[#00E5FF]/25 hover:shadow-[#00E5FF]/40 hover:-translate-y-0.5 group"
              >
                Start Free — No Card Needed
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <button
                onClick={() => document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center gap-2 border border-white/10 hover:border-white/20 bg-white/[0.04] hover:bg-white/[0.07] text-slate-300 font-medium px-7 py-3.5 rounded-xl transition-all"
              >
                <Play size={14} className="text-[#00E5FF]" />
                See How It Works
              </button>
            </div>

            {/* Social proof mini */}
            <div className="animate-fade-in delay-400 flex items-center gap-4">
              <div className="flex -space-x-2">
                {['A','M','K','R','J'].map((l, i) => (
                  <div
                    key={i}
                    className="w-7 h-7 rounded-full border-2 border-[#0B0F19] flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ background: ['#00E5FF','#7C3AED','#06b6d4','#8b5cf6','#0ea5e9'][i] }}
                  >
                    {l}
                  </div>
                ))}
              </div>
              <div className="text-slate-500 text-sm">
                <span className="text-white font-semibold">500+</span> security teams trust ZenithCipher
              </div>
            </div>
          </div>

          {/* Right — dashboard mockup */}
          <div className="animate-fade-in delay-300 relative lg:pl-8">
            {/* Floating badge — top right */}
            <div className="absolute -top-4 -right-2 z-10 animate-float bg-[#0a1020] border border-[#00E5FF]/20 rounded-xl px-3 py-2 shadow-xl shadow-black/40">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle size={11} className="text-red-400" />
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-red-400">HIGH RISK</div>
                  <div className="text-[9px] text-slate-500">Risk Score: 78/100</div>
                </div>
              </div>
            </div>

            {/* Floating badge — bottom left */}
            <div className="absolute -bottom-4 -left-2 z-10 animate-float-slow bg-[#0a1020] border border-green-500/20 rounded-xl px-3 py-2 shadow-xl shadow-black/40">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle size={11} className="text-green-400" />
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-green-400">Scan Complete</div>
                  <div className="text-[9px] text-slate-500">0.8s response time</div>
                </div>
              </div>
            </div>

            {/* Glow ring behind mockup */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/10 to-[#7C3AED]/10 rounded-2xl blur-2xl scale-95 pointer-events-none" />

            <DashboardMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

/* 2 ── TRUST BAR */
function TrustSection() {
  const logos = [
    'DeFi Protocol', 'CryptoExchange', 'BlockSafe', 'ChainGuard',
    'Web3 Ventures', 'SecureDAO', 'TokenOps', 'AuditFirm',
  ];

  return (
    <section className="py-12 border-y border-white/[0.05] bg-[#080c16]/60">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <p className="text-center text-xs font-semibold text-slate-600 uppercase tracking-widest mb-8">
          Trusted by Web3 developers, exchanges &amp; security teams
        </p>
        <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-4">
          {logos.map(name => (
            <div
              key={name}
              className="text-slate-600 hover:text-slate-400 text-sm font-semibold tracking-wide transition-colors cursor-default"
            >
              {name}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* 3 ── FEATURES */
const FEATURES = [
  {
    icon: Search,
    title: 'Wallet Risk Scanner',
    desc: 'Instantly analyze any Ethereum wallet. Get risk scores, warnings, and on-chain behavioral patterns in under a second.',
    badge: 'Core',
    accent: '#00E5FF',
  },
  {
    icon: AlertTriangle,
    title: 'Fraud Detection',
    desc: 'Identify mixer usage, scam tokens, phishing-linked addresses, and bot-like transaction patterns automatically.',
    badge: 'AI-Powered',
    accent: '#f43f5e',
  },
  {
    icon: BarChart3,
    title: 'Transaction Intelligence',
    desc: 'Deep behavioral analysis of wallet history — transaction volume, contract calls, token transfers, and anomaly detection.',
    badge: 'Analytics',
    accent: '#7C3AED',
  },
  {
    icon: Code2,
    title: 'Developer API',
    desc: 'REST API with Bearer token auth. Integrate wallet risk scoring directly into your dApp, exchange, or compliance stack.',
    badge: 'API',
    accent: '#f59e0b',
  },
];

function FeaturesSection() {
  return (
    <section id="features" className="py-28 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#7C3AED]/3 to-transparent pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">

        <div className="text-center mb-16 reveal">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#7C3AED] bg-[#7C3AED]/10 border border-[#7C3AED]/20 px-3 py-1.5 rounded-full mb-4">
            <Zap size={11} /> Platform Capabilities
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Everything your team needs to<br />
            <span className="text-[#00E5FF]">secure Web3 operations</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            From one-click wallet scans to deep transaction intelligence — built for speed, accuracy, and scale.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map(({ icon: Icon, title, desc, badge, accent }, i) => (
            <div
              key={title}
              className={`reveal group glass-card p-6 hover:border-opacity-30 transition-all duration-300 hover:-translate-y-1 cursor-default`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Icon */}
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 transition-all duration-300 group-hover:scale-110"
                style={{ background: `${accent}18`, border: `1px solid ${accent}28` }}
              >
                <Icon size={19} style={{ color: accent }} />
              </div>

              {/* Badge */}
              <div
                className="text-[10px] font-bold px-2 py-0.5 rounded-full mb-3 inline-block"
                style={{ background: `${accent}15`, color: accent }}
              >
                {badge}
              </div>

              <h3 className="text-base font-bold text-white mb-2">{title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>

              {/* Hover arrow */}
              <div className="mt-5 flex items-center gap-1 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: accent }}>
                Learn more <ChevronRight size={12} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* 4 ── HOW IT WORKS */
const STEPS = [
  {
    n: '01',
    icon: Search,
    title: 'Enter Wallet Address',
    desc: 'Paste any Ethereum wallet address (0x…) into the scanner or send it via our REST API.',
    color: '#00E5FF',
  },
  {
    n: '02',
    icon: Activity,
    title: 'Analyze Blockchain Data',
    desc: 'We query Etherscan in real-time — transactions, token transfers, contract calls, and known bad-actor lists.',
    color: '#7C3AED',
  },
  {
    n: '03',
    icon: Shield,
    title: 'Get Instant Risk Report',
    desc: 'Receive a scored report: risk level (Low/Medium/High), warnings, reputation score, and flagged tokens.',
    color: '#00E5FF',
  },
];

function HowItWorksSection() {
  return (
    <section id="how-it-works" className="py-28 bg-[#080c16]/70">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">

        <div className="text-center mb-16 reveal">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/20 px-3 py-1.5 rounded-full mb-4">
            <Globe size={11} /> Simple 3-Step Process
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            From address to insight<br />
            <span className="text-[#00E5FF]">in under a second</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-lg mx-auto">
            No setup required. Paste a wallet, hit scan, get your risk report immediately.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connector line */}
          <div className="hidden md:block absolute top-10 left-[16.67%] right-[16.67%] h-px bg-gradient-to-r from-[#00E5FF]/30 via-[#7C3AED]/30 to-[#00E5FF]/30" />

          <div className="grid md:grid-cols-3 gap-10">
            {STEPS.map(({ n, icon: Icon, title, desc, color }, i) => (
              <div key={n} className={`reveal text-center`} style={{ transitionDelay: `${i * 120}ms` }}>
                {/* Step number circle */}
                <div className="relative inline-flex items-center justify-center mb-7">
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center"
                    style={{ background: `${color}12`, border: `1px solid ${color}28` }}
                  >
                    <Icon size={28} style={{ color }} />
                  </div>
                  <div
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full text-[10px] font-black flex items-center justify-center text-black"
                    style={{ background: color }}
                  >
                    {i + 1}
                  </div>
                </div>

                <div className="text-[10px] font-bold tracking-widest mb-2" style={{ color }}>
                  STEP {n}
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed max-w-xs mx-auto">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-14 reveal">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00cce5] text-black font-bold px-7 py-3.5 rounded-xl transition-all shadow-lg shadow-[#00E5FF]/20 hover:shadow-[#00E5FF]/40 hover:-translate-y-0.5 group"
          >
            Try It Free <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* 5 ── DASHBOARD PREVIEW */
function DashboardPreviewSection() {
  const benefits = [
    { icon: TrendingUp, label: 'Risk Scoring',    desc: '0–100 risk score with clear High/Medium/Low classification.' },
    { icon: Eye,        label: 'Scan History',     desc: 'Full audit trail of every wallet you have analyzed.' },
    { icon: BarChart3,  label: 'Analytics',        desc: 'Charts, trends, and pattern recognition across scans.' },
    { icon: Shield,     label: 'Fraud Alerts',     desc: 'Real-time flags for mixer usage, scam tokens, and bad actors.' },
  ];

  return (
    <section className="py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="glass-card cyber-border overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-0">

            {/* Left: benefits */}
            <div className="p-10 lg:p-14 flex flex-col justify-center reveal-left">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/20 px-3 py-1.5 rounded-full mb-6 w-fit">
                <Activity size={11} /> Live Dashboard
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5 leading-tight">
                Everything in one
                <span className="text-[#00E5FF]"> security dashboard</span>
              </h2>
              <p className="text-slate-400 text-base leading-relaxed mb-10">
                Your command center for blockchain risk intelligence. Monitor scans, track risk trends,
                manage API keys, and export reports — all in one place.
              </p>
              <div className="space-y-5">
                {benefits.map(({ icon: Icon, label, desc }) => (
                  <div key={label} className="flex items-start gap-4">
                    <div className="w-9 h-9 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon size={15} className="text-[#00E5FF]" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white mb-0.5">{label}</div>
                      <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00cce5] text-black font-bold px-6 py-3 rounded-xl transition-all shadow-lg shadow-[#00E5FF]/20 hover:-translate-y-0.5 group text-sm"
                >
                  Open Dashboard <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Right: mockup */}
            <div className="relative bg-[#050810] lg:border-l border-white/[0.06] p-8 flex items-center justify-center reveal-right">
              <div className="absolute inset-0 hero-grid opacity-40 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/5 to-[#7C3AED]/5 pointer-events-none" />
              <div className="relative w-full max-w-sm">
                <DashboardMockup />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* 6 ── USE CASES */
const USE_CASES = [
  {
    icon: TrendingUp,
    title: 'Crypto Traders',
    desc: 'Verify counterparty wallets before transferring assets. Avoid interacting with high-risk or blacklisted addresses.',
    examples: ['Verify OTC counterparties', 'Pre-trade due diligence', 'Spot bot wallets'],
    color: '#00E5FF',
  },
  {
    icon: Building2,
    title: 'Web3 Companies',
    desc: 'Protect your protocol from malicious actors. Screen user wallets during onboarding or token sales.',
    examples: ['User wallet screening', 'IDO/whitelist validation', 'Airdrop protection'],
    color: '#7C3AED',
  },
  {
    icon: Eye,
    title: 'Security Analysts',
    desc: 'Investigate suspicious on-chain activity. Deep-dive into wallet behavior, transaction flows, and token patterns.',
    examples: ['Incident investigation', 'Threat intelligence', 'Behavioral profiling'],
    color: '#f43f5e',
  },
  {
    icon: Lock,
    title: 'Compliance Teams',
    desc: "Meet AML/KYC requirements for blockchain. Generate audit-ready reports with detailed risk evidence.",
    examples: ['AML screening', 'Regulatory reporting', 'Audit-ready exports'],
    color: '#f59e0b',
  },
];

function UseCasesSection() {
  return (
    <section className="py-28 bg-[#080c16]/50">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">

        <div className="text-center mb-16 reveal">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#7C3AED] bg-[#7C3AED]/10 border border-[#7C3AED]/20 px-3 py-1.5 rounded-full mb-4">
            <Users size={11} /> Who Uses ZenithCipher
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Built for every team in
            <span className="text-[#7C3AED]"> Web3 security</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            From individual traders to enterprise compliance desks — one platform, every use case.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {USE_CASES.map(({ icon: Icon, title, desc, examples, color }, i) => (
            <div
              key={title}
              className="reveal glass-card p-6 group hover:-translate-y-1 transition-all duration-300 cursor-default"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"
                style={{ background: `${color}15`, border: `1px solid ${color}25` }}
              >
                <Icon size={19} style={{ color }} />
              </div>
              <h3 className="text-base font-bold text-white mb-2">{title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-5">{desc}</p>
              <ul className="space-y-1.5">
                {examples.map(ex => (
                  <li key={ex} className="flex items-center gap-2 text-xs text-slate-500">
                    <CheckCircle size={11} style={{ color }} className="flex-shrink-0" />
                    {ex}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* 7 ── PRICING */
const PLANS = [
  {
    name: 'Free',
    price: '$0',
    period: '/mo',
    desc: 'Perfect for exploring ZenithCipher.',
    features: ['10 scans / day', '1 API key', 'Basic risk scoring', '7-day history', 'Community support'],
    cta: 'Get Started Free',
    color: 'slate',
  },
  {
    name: 'Starter',
    price: '$29',
    period: '/mo',
    desc: 'For active traders and small teams.',
    features: ['100 scans / day', '3 API keys', 'Advanced scoring', '30-day history', 'JSON exports', 'Email support'],
    cta: 'Start Starter',
    color: 'cyan',
    popular: true,
  },
  {
    name: 'Pro',
    price: '$99',
    period: '/mo',
    desc: 'For security teams and power users.',
    features: ['Unlimited scans', '10 API keys', 'Full analysis suite', 'Unlimited history', 'Webhook alerts', 'Priority support', 'Team seats'],
    cta: 'Start Pro',
    color: 'purple',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    desc: 'For compliance teams and large orgs.',
    features: ['Unlimited everything', 'Custom integrations', 'SLA guarantee', 'Dedicated support', 'On-premise option', 'Custom contracts'],
    cta: 'Contact Sales',
    color: 'slate',
  },
];

function PricingSection() {
  return (
    <section id="pricing" className="py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">

        <div className="text-center mb-16 reveal">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/20 px-3 py-1.5 rounded-full mb-4">
            <Zap size={11} /> Simple Pricing
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Start free, scale as you grow
          </h2>
          <p className="text-slate-400 text-lg max-w-md mx-auto">
            No hidden fees. Cancel anytime. Free plan includes 10 scans per day — no credit card required.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 items-start">
          {PLANS.map(({ name, price, period, desc, features, cta, color, popular }, i) => (
            <div
              key={name}
              className={[
                'reveal glass-card flex flex-col transition-all duration-300',
                popular ? 'border-[#00E5FF]/30 glow-cyan relative' : '',
              ].join(' ')}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#00E5FF] text-black text-[10px] font-black px-3 py-1 rounded-full flex items-center gap-1 whitespace-nowrap shadow-lg">
                  <Star size={9} fill="black" /> Most Popular
                </div>
              )}

              <div className="p-6 flex-1">
                <div className="mb-5">
                  <div className="text-xs font-semibold text-slate-500 mb-1">{name}</div>
                  <div className="flex items-end gap-1 mb-1">
                    <span className="text-3xl font-bold text-white">{price}</span>
                    {period && <span className="text-slate-500 text-sm pb-1">{period}</span>}
                  </div>
                  <div className="text-xs text-slate-600">{desc}</div>
                </div>

                <ul className="space-y-2 mb-6">
                  {features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-xs text-slate-500">
                      <CheckCircle size={11} className="text-[#00E5FF] flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="px-6 pb-6">
                <Link
                  to={name === 'Enterprise' ? '#' : '/register'}
                  className={[
                    'block w-full text-center py-2.5 rounded-xl text-sm font-bold transition-all',
                    color === 'cyan'   ? 'bg-[#00E5FF] hover:bg-[#00cce5] text-black shadow-lg shadow-[#00E5FF]/20' :
                    color === 'purple' ? 'bg-[#7C3AED] hover:bg-[#6d35d4] text-white' :
                                        'bg-white/[0.07] hover:bg-white/[0.12] text-slate-300 border border-white/[0.08]',
                  ].join(' ')}
                >
                  {cta}
                </Link>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-slate-600 mt-8">
          All plans include SSL, CORS-secured API, and rate limiting. Prices in USD.
        </p>
      </div>
    </section>
  );
}

/* 8 ── TESTIMONIALS */
const TESTIMONIALS = [
  {
    quote: "ZenithCipher caught wallet risks our previous tools completely missed. The risk scoring is incredibly accurate — it's now a mandatory step in our compliance flow.",
    name: 'Alex Chen',
    role: 'Head of Security, CryptoExchange',
    avatar: 'A',
    stars: 5,
  },
  {
    quote: "We integrated the API in under an hour. Now every wallet in our IDO whitelist is automatically screened. The Developer API documentation is excellent.",
    name: 'Maria Lopez',
    role: 'CTO, DeFi Protocol',
    avatar: 'M',
    stars: 5,
  },
  {
    quote: "The fraud detection is what sets ZenithCipher apart. It caught mixer-linked wallets and scam tokens that would have caused serious issues during our token launch.",
    name: 'Kai Yamamoto',
    role: 'Security Analyst, Web3 Ventures',
    avatar: 'K',
    stars: 5,
  },
];

function TestimonialsSection() {
  return (
    <section className="py-28 bg-[#080c16]/40">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">

        <div className="text-center mb-14 reveal">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Trusted by security professionals
          </h2>
          <p className="text-slate-400 text-lg">Don't take our word for it — here's what teams say.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map(({ quote, name, role, avatar, stars }, i) => (
            <div
              key={name}
              className="reveal glass-card p-6 hover:-translate-y-1 transition-all duration-300"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {Array.from({ length: stars }).map((_, j) => (
                  <Star key={j} size={12} className="text-[#00E5FF] fill-[#00E5FF]" />
                ))}
              </div>

              <p className="text-slate-300 text-sm leading-relaxed mb-6 italic">
                "{quote}"
              </p>

              <div className="flex items-center gap-3 pt-4 border-t border-white/[0.05]">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#7C3AED] flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                  {avatar}
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">{name}</div>
                  <div className="text-xs text-slate-500">{role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* 9 ── FINAL CTA */
function CTASection() {
  return (
    <section className="py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/6 via-transparent to-[#7C3AED]/6 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-1 bg-gradient-to-r from-transparent via-[#00E5FF]/40 to-transparent" />

      <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative reveal">
        <div className="glass-card cyber-border p-14 sm:p-20">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00E5FF] to-[#7C3AED] flex items-center justify-center mx-auto mb-8 glow-cyan">
            <Shield size={26} className="text-white" />
          </div>

          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-5 leading-tight">
            Start securing your Web3
            <span className="block text-[#00E5FF]">experience today</span>
          </h2>

          <p className="text-slate-400 text-lg max-w-lg mx-auto mb-10">
            Join 500+ teams using ZenithCipher for wallet risk analysis and blockchain intelligence.
            Free plan — no credit card required.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-[#00E5FF] hover:bg-[#00cce5] text-black font-bold px-8 py-4 rounded-xl transition-all shadow-xl shadow-[#00E5FF]/25 hover:shadow-[#00E5FF]/40 hover:-translate-y-0.5 text-sm group"
            >
              Create Free Account
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 border border-white/10 hover:border-white/20 bg-white/[0.04] hover:bg-white/[0.07] text-slate-300 font-medium px-8 py-4 rounded-xl transition-all text-sm"
            >
              Sign In
            </Link>
          </div>

          {/* Mini trust signals */}
          <div className="flex flex-wrap justify-center gap-6 mt-10 pt-8 border-t border-white/[0.05]">
            {[
              '✓  No credit card needed',
              '✓  10 free scans per day',
              '✓  Cancel anytime',
              '✓  SOC2-ready API',
            ].map(t => (
              <span key={t} className="text-xs text-slate-500">{t}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Root LandingPage
───────────────────────────────────────────────────────────────────────── */
export default function LandingPage() {
  useReveal();   // activate scroll-reveal on all .reveal elements

  return (
    <div className="min-h-screen bg-[#0B0F19] overflow-x-hidden text-white">
      <Navbar />
      <HeroSection />
      <TrustSection />
      <FeaturesSection />
      <HowItWorksSection />
      <DashboardPreviewSection />
      <UseCasesSection />
      <PricingSection />
      <TestimonialsSection />
      <CTASection />
      <Footer />
    </div>
  );
}
